/** EA authentication adapter for the shared sign-in presentation. */

import { useState } from 'react'
import type { CSSProperties, FormEvent } from 'react'
import { useAuth } from '../auth'
import {
  LOGIN_PRIMARY,
  MS_BLUE,
  MS_GREEN,
  MS_RED,
  MS_YELLOW,
} from './loginPalette'
import { FLUENT_DARK, FLUENT_LIGHT, availableThemes } from '../theme'
import { useThemeChoice } from '../themeContext'
import { SignInLayout } from './SignInLayout'

/** One message, whatever the cause, because that is what the service answers. */
const REFUSED = 'Email or password is incorrect.'

export function SignIn() {
  const { tenantRegistered, signIn, signInWithPassword } = useAuth()
  const { themeId, chooseTheme } = useThemeChoice()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [failed, setFailed] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const base = availableThemes().find((theme) => theme.id === themeId)?.base ?? 'dark'
  const primary: CSSProperties = { backgroundColor: LOGIN_PRIMARY[base] }
  const toggleLabel =
    base === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    const problem = await signInWithPassword(email, password)
    setFailed(problem ? REFUSED : null)
    setBusy(false)
  }

  return (
    <SignInLayout
      brand={{
        serviceName: 'EA Service Authorization',
        subtitle: 'Every authorization passes through here',
        description: 'Which token the station sent, who was asked, and what this service answered.',
      }}
      title="Sign in"
      error={failed}
      fields={[
        {
          id: 'signin-email',
          label: 'Email',
          type: 'email',
          value: email,
          autoFocus: true,
          autoComplete: 'username',
          placeholder: 'you@wallefleet.de',
          onChange: setEmail,
        },
        {
          id: 'signin-password',
          label: 'Password',
          type: 'password',
          value: password,
          autoComplete: 'current-password',
          onChange: setPassword,
        },
      ]}
      primaryAction={{
        label: busy ? 'Signing in...' : 'Sign in',
        busy,
        disabled: busy || !email || !password,
        style: primary,
      }}
      dividerLabel="or"
      secondaryAction={{
        label: 'Sign in with Microsoft',
        disabled: !tenantRegistered,
        onClick: signIn,
        status: tenantRegistered ? null : 'Waiting for tenant registration',
        icon: <MicrosoftGlyph />,
      }}
      footnote={
        <>
          Passwords are set by an administrator on the host. There is no self service and no reset
          here, and this way in goes away once the tenant is ready.
        </>
      }
      themeControl={{
        label: toggleLabel,
        glyph: base === 'dark' ? '☀' : '☾',
        onClick: () => chooseTheme(base === 'dark' ? FLUENT_LIGHT : FLUENT_DARK),
      }}
      onSubmit={submit}
    />
  )
}

function MicrosoftGlyph() {
  return (
    <span
      aria-hidden
      style={{
        display: 'grid',
        gridTemplateColumns: '8px 8px',
        gridTemplateRows: '8px 8px',
        gap: '2px',
      }}
    >
      <span style={{ backgroundColor: MS_RED }} />
      <span style={{ backgroundColor: MS_GREEN }} />
      <span style={{ backgroundColor: MS_BLUE }} />
      <span style={{ backgroundColor: MS_YELLOW }} />
    </span>
  )
}
